import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { DecimalPipe, LowerCasePipe, TitleCasePipe } from '@angular/common';
import { Component, DestroyRef, TemplateRef, ViewContainerRef, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  PropertyDocumentStatus,
  PropertyDocumentType,
  PropertyDto,
  PropertyVerificationStatus,
} from '@workspace/data-types';
import {
  AvatarAuraComponent,
  BaseDataTableCoreComponent,
  DataTableComponent,
  LuxonDatePipe,
} from '@workspace/ng-aura';
import { StateStatusDisplayPipe } from '@workspace/ng-data-access';
import { EtxeEnumDisplayPipe } from '../../pipes/etxe-enum-display.pipe';
import { PropertyHoverCardComponent, PropertyHoverPreviewDirective } from '../property-hover-card';
import { PropertyColumnKey } from './abstract-properties-data-table.component';
import { PropertiesDataTableFilterCoreComponent } from './filter/properties-data-table-filter.shared.component';

@Component({
  selector: 'etxe-properties-data-table',
  standalone: true,
  templateUrl: './properties-data-table.shared.component.html',
  imports: [
    DataTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatButtonModule,
    MatCellDef,
    MatCell,
    LuxonDatePipe,
    LowerCasePipe,
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    EtxeEnumDisplayPipe,
    StateStatusDisplayPipe,
    PropertiesDataTableFilterCoreComponent,
    AvatarAuraComponent,
    TitleCasePipe,
    PropertyHoverCardComponent,
    PropertyHoverPreviewDirective,
  ],
})
export class PropertiesDataTableCoreComponent extends BaseDataTableCoreComponent<
  PropertyDto,
  PropertyColumnKey
> {
  hideSearch = input<boolean>(false);
  searchPlaceHolder = input<string>('Rechercher par référence, titre, ville, ID...');

  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);

  private overlayRef: OverlayRef | null = null;

  constructor() {
    super();
    this.destroyRef.onDestroy(() => {
      this.hidePropertyPreview();
    });
  }

  showPropertyPreview(property: PropertyDto, event: MouseEvent, tooltipTemplate: TemplateRef<unknown>) {
    this.hidePropertyPreview();

    const target = event.currentTarget as HTMLElement;
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(target)
      .withPositions([
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 16,
        },
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 8,
        },
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetX: 16,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -8,
        },
      ])
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: ['pointer-events-none', 'z-50'],
    });

    const portal = new TemplatePortal(tooltipTemplate, this.vcr, {
      $implicit: property,
    });
    this.overlayRef.attach(portal);
  }

  hidePropertyPreview() {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  getOwnerVerificationStatus(property: PropertyDto): PropertyVerificationStatus {
    return property.verification?.owner ?? PropertyVerificationStatus.UNVERIFIED;
  }

  getOwnershipDocumentStatus(property: PropertyDto): PropertyDocumentStatus | null {
    const ownershipDocs = property.documents?.filter((d) => d.type === PropertyDocumentType.OWNERSHIP) || [];
    return ownershipDocs.length > 0 ? ownershipDocs[ownershipDocs.length - 1].status : null;
  }

  getDpeVerificationStatus(property: PropertyDto): PropertyVerificationStatus | null {
    return property.verification?.dpe ?? null;
  }

  hasPendingReviewDocuments(property: PropertyDto): boolean {
    return property.documents?.some((d) => d.status === PropertyDocumentStatus.PENDING_REVIEW) ?? false;
  }

  hasRejectedDocuments(property: PropertyDto): boolean {
    return property.documents?.some((d) => d.status === PropertyDocumentStatus.REJECTED) ?? false;
  }

  hasDocuments(property: PropertyDto): boolean {
    return (property.documents?.length ?? 0) > 0;
  }

  hasModerationFlag(property: PropertyDto): boolean {
    return property.moderation?.hasContactFlag ?? false;
  }
}
